'use client';

import Image from 'next/image';
import Link from 'next/link';
import { PropsWithChildren } from 'react';

import NewsletterSubscription from '../NewsletterSubscription';

import cards from '../../../public/cards.svg';
import logo from '../../../public/logo-mobile.svg';
import symbol from '../../../public/symbol.svg';
import waves from '../../../public/wave.svg';

import { HeaderDropdowns } from '@/components/NewHeader/HeaderDropdowns';
import { useTranslation } from '@/contexts/dictionaryContext';
import { useLocale } from '@/hooks/useLocale';
import { projectInfo } from '@/utils/project-info';
import { social } from '@/utils/social';

interface FooterListProps extends PropsWithChildren {
  header: string;
  sections?: { label: string; path: string }[];
}

function FooterList({ children, header, sections }: FooterListProps) {
  return (
    <ul className="flex flex-col justify-start gap-[0.8rem]">
      <li className="font-semibold uppercase pb-[0.8rem] tracking-widest text-sm pr-2">
        {header}
      </li>
      {sections?.map(({ label, path }) => {
        return (
          <li className="hover:text-white" key={label}>
            <Link href={path}>{label}</Link>
          </li>
        );
      })}
      {children}
    </ul>
  );
}

export default function Footer() {
  const { footer } = useTranslation();
  const lang = useLocale();

  return (
    <footer
      id="main-footer"
      className="w-full text-footer-gray bg-footer-graphite pt-2">
      <Image className="pb-4 opacity-70" src={waves} alt="Waves image" />
      <div className="max-w-default m-auto px-3 md:px-md desktop:px-lg md:flex md:items-start md:flex-wrap md:justify-between">
        <div className="flex flex-col justify-start md:w-[33%] lg:w-[30%] desktop:w-[23%] mb-8">
          <a className="pb-4" href="/">
            <Image
              src={logo}
              loading="eager"
              sizes="(max-width: 1000px) 100vw, 85vw"
              priority
              alt="Villatravellers text image"
            />
          </a>
          <div>
            Villatravellers S.r.l.
            <br />
            Via Giovanni Campolo, 40
            <br />
            90145 Palermo, {footer.sections.villaTravellers.italy}
            <br />
          </div>
          <br />
          <div>
            <a
              className="hover:text-white"
              href="mailto:info@villatravellers.com">
              info@villatravellers.com
            </a>
            <br />
            {'IT: '}
            <a className="hover:text-white" href="tel:00390916870639">
              0039 091 687 0639
            </a>
            <br />
            {'UK: '}
            <a className="hover:text-white" href="tel:0044(0)2036084505">
              0044 (0)20 3608 4505
            </a>
            <br />
            {'DE: '}
            <a className="hover:text-white" href="tel:0049(0)3056795949">
              0049 (0)30 5679 5949
            </a>
          </div>
          <br />
          <div className="flex items-center gap-4">
            <a className="hover:text-white" href={footer.contactPath}>
              {footer.contact}
            </a>
            <a className="hover:text-white" href={footer.aboutUsPath}>
              {footer.aboutUs}
            </a>
          </div>

          <div className="relative pt-4 flex gap-2">
            <HeaderDropdowns />
          </div>
        </div>
        <div className="mb-12 md:w-[33%] lg:w-[30%] desktop:w-[14%]">
          <FooterList
            sections={footer.sections.goodToKnow.options}
            header={footer.sections.goodToKnow.title}>
            <li>
              <Image src={cards} alt="Credit cards brands" />
            </li>
          </FooterList>
        </div>
        <div className="mb-12 md:w-[33%] desktop:w-[14%]">
          <div className="flex flex-col gap-8">
            <FooterList
              sections={footer.sections.sicily.options}
              header={footer.sections.sicily.title}
            />

            <FooterList
              sections={footer.sections.puglia.options}
              header={footer.sections.puglia.title}
            />
          </div>
        </div>
        <div className="mb-12 desktop:w-[14%]">
          <FooterList
            sections={footer.sections.ourVillas.options}
            header={footer.sections.ourVillas.title}
          />
        </div>

        <NewsletterSubscription />

        <a
          href={lang === 'en' ? '/' : `/${lang}`}
          className="hidden desktop:w-full desktop:flex desktop:justify-center desktop:items-center desktop:p-16">
          <figure>
            <Image
              width={80}
              height={80}
              src={symbol}
              alt="Villatravelers Icon"
            />
          </figure>
        </a>
        <div className="lg:w-full desktop:flex desktop:justify-between">
          <ul className="flex flex-col">
            <li className="font-semibold list-none uppercase pb-3 tracking-widest text-sm">
              {footer.sections.partnership.title}
            </li>
            <li className="flex flex-wrap items-center">
              <a target="_blank" href="https://www.aiav.eu/">
                <Image
                  className="pb-12 pr-8"
                  width={120}
                  height={80}
                  alt="AIAV logo image"
                  src="https://villatravellers-prod.s3.eu-west-2.amazonaws.com/assets/images/partners/aiav-white@2x.png"
                />
              </a>
              <Image
                className="pb-12 pr-8"
                width={78}
                height={85}
                alt="ASTA logo image"
                src="https://villatravellers-prod.s3.eu-west-2.amazonaws.com/assets/images/partners/asta-logo.svg"
              />
              <a target="_blank" href="https://abtoi.com/villatravellers/">
                <Image
                  className="pb-12 pr-8"
                  width={160}
                  height={70}
                  alt="Abtoi logo image"
                  src="https://villatravellers-prod.s3.eu-west-2.amazonaws.com/assets/images/partners/Abtoi-logo@2x.png"
                />
              </a>
              <a target="_blank" href="https://www.italchamind.org.uk/it/">
                <Image
                  className="pb-12 pr-8"
                  width={185}
                  height={60}
                  alt="Italchamind logo image"
                  src="https://villatravellers-prod.s3.eu-west-2.amazonaws.com/assets/images/partners/chamber-white@2x.png"
                />
              </a>
              <a
                target="_blank"
                href="https://www.trustpilot.com/review/www.villatravellers.com">
                <Image
                  className="pb-12 pr-8"
                  width={185}
                  height={70}
                  alt="Trustpilot image logo"
                  src="https://villatravellers-prod.s3.eu-west-2.amazonaws.com/assets/images/partners/trustpilot-white.svg"
                />
              </a>
            </li>
          </ul>
          <div className="mb-12 md:mb-0 md:w-full desktop:w-fit">
            <ul className="flex flex-col">
              <li className="font-semibold list-none uppercase pb-3 tracking-widest text-sm">
                {footer.sections.downloadApp.title}
              </li>
              <li className="flex flex-wrap items-center gap-3">
                <a
                  target="_blank"
                  href="https://apps.apple.com/it/app/villatravellers/id6443793329">
                  <Image
                    width={140}
                    height={42}
                    className="h-auto"
                    alt="Link to download the site app at App Store"
                    src="https://villatravellers-prod.s3.eu-west-2.amazonaws.com/assets/images/download-app/app-store-badge.png"
                  />
                </a>
                <a
                  target="_blank"
                  href="https://play.google.com/store/apps/details?id=com.villatravellers.www">
                  <Image
                    width={140}
                    height={42}
                    className="h-auto"
                    alt="Link to download the site app at Google Play"
                    src="https://villatravellers-prod.s3.eu-west-2.amazonaws.com/assets/images/download-app/play-store-badge.png"
                  />
                </a>
              </li>
            </ul>
            <div className="hidden desktop:hidden md:flex md:justify-center md:items-center md:p-16">
              <figure>
                <Image
                  width={40}
                  height={40}
                  src={symbol}
                  alt="Villatravelers Icon"
                />
              </figure>
            </div>
          </div>
        </div>

        <div className="flex flex-col md:flex-row-reverse md:pb-12 md:justify-between lg:w-full">
          <div className="flex mb-8 sm:mb-4 mt-auto gap-4 mr-2">
            <Link
              href={social.facebook.link}
              target="_blank"
              rel="noopener noreferrer">
              <Image
                src={social.facebook.imgPath}
                alt={social.facebook.alt}
                width={18}
                height={18}
                className="brightness-[.6] transition duration-300 hover:brightness-[1.5]"
              />
            </Link>
            <Link
              href={social.x.link}
              target="_blank"
              rel="noopener noreferrer">
              <Image
                src={social.x.imgPath}
                alt={social.x.alt}
                width={18}
                height={18}
                className="brightness-[.6] transition duration-300 hover:brightness-[1.5]"
              />
            </Link>
            <Link
              href={social.instagram.link}
              target="_blank"
              rel="noopener noreferrer">
              <Image
                src={social.instagram.imgPath}
                alt={social.instagram.alt}
                width={18}
                height={18}
                className="brightness-[.6] transition duration-300 hover:brightness-[1.5]"
              />
            </Link>
            <Link
              href={social.youtube.link}
              target="_blank"
              rel="noopener noreferrer">
              <Image
                src={social.youtube.imgPath}
                alt={social.youtube.alt}
                width={18}
                height={18}
                className={
                  'h-full brightness-[.6] transition duration-300 hover:brightness-[1.5]'
                }
              />
            </Link>
            <Link
              href={social.spotify.link}
              target="_blank"
              rel="noopener noreferrer">
              <Image
                src={social.spotify.imgPath}
                alt={social.spotify.alt}
                width={18}
                height={18}
                className="brightness-[.6] transition duration-300 hover:brightness-[1.5]"
              />
            </Link>
            <Link href={social.linkedin.link} target="_blank">
              <Image
                src={social.linkedin.imgPath}
                alt={social.linkedin.alt}
                width={18}
                height={18}
                className="brightness-[.6] transition duration-300 hover:brightness-[1.5]"
              />
            </Link>
          </div>
          <div className="text-sm">
            <p className="mb-4">
              {`v${
                projectInfo.projectVersion
              } ©Copyright ${new Date().getFullYear()}. ${
                footer.terms.rightsReserved
              } `}
              {' – '}
              <Link href={footer.terms.cookiePath} className="mx-1">
                {footer.terms.cookiePolicy}
              </Link>
              {' – '}
              <Link href={footer.terms.privacyPath}>
                {footer.terms.privacyPolicy}
              </Link>
              {' – '}
              <Link href={footer.terms.conditionsPath}>
                {footer.terms.conditions}
              </Link>
            </p>
          </div>
        </div>
        <div className="flex justify-center items-center p-16 md:hidden">
          <figure>
            <Image
              width={40}
              height={40}
              src={symbol}
              alt="Villatravelers Icon"
            />
          </figure>
        </div>
      </div>
    </footer>
  );
}
